import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material';
import { useCallback, useState } from 'react';

interface ReaxffElementFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedElements: string[]) => void;
  selectedElements: string[];
  availableElements: Set<string>;
  remainingElements: Set<string>;
}

// Periodic Table layout (simplified for chips, left-padding as empty strings)
const PERIODIC_TABLE_LAYOUT: string[][] = [
  ['H', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'He'],
  ['Li', 'Be', '', '', '', '', '', '', '', '', '', '', 'B', 'C', 'N', 'O', 'F', 'Ne'],
  ['Na', 'Mg', '', '', '', '', '', '', '', '', '', '', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar'],
  ['K', 'Ca', 'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn', 'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr'],
  ['Rb', 'Sr', 'Y', 'Zr', 'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd', 'In', 'Sn', 'Sb', 'Te', 'I', 'Xe'],
  ['Cs', 'Ba', 'La', 'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg', 'Tl', 'Pb', 'Bi', 'Po', 'At', 'Rn'],
  ['Fr', 'Ra', 'Ac', 'Th', 'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf', 'Es', 'Fm', 'Md', 'No', 'Lr'],
];

export function ReaxffElementFilterDialog({
  open,
  onClose,
  onApply,
  selectedElements: initialSelectedElements,
  availableElements,
  remainingElements,
}: ReaxffElementFilterDialogProps) {
  const [selectedElements, setSelectedElements] = useState<string[]>(initialSelectedElements);

  // Discard draft selection and notify parent (cancel, backdrop, escape).
  const handleDismiss = useCallback(() => {
    setSelectedElements(initialSelectedElements);
    onClose();
  }, [initialSelectedElements, onClose]);

  const handleDialogClose = useCallback(
    (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  const toggleElement = (el: string) => {
    if (!availableElements.has(el)) return;
    setSelectedElements((prev) => (prev.includes(el) ? prev.filter((e) => e !== el) : [...prev, el]));
  };

  const resetElements = () => {
    setSelectedElements([]);
  };

  const handleApply = () => {
    onApply(selectedElements);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>Filter by Elements</DialogTitle>
      <DialogContent dividers>
        <Typography fontWeight={600} gutterBottom>
          Elements
        </Typography>
        <Box display="flex" justifyContent="center" mb={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(18, 36px)',
              gap: 0.5,
            }}
          >
            {PERIODIC_TABLE_LAYOUT.flat().map((el, idx) =>
              el === '' ? (
                <Box key={`empty-${idx}`} sx={{ width: 36, height: 32 }} />
              ) : (
                <Chip
                  key={el}
                  label={el}
                  clickable={availableElements.has(el)}
                  onClick={() => toggleElement(el)}
                  color={selectedElements.includes(el) ? 'primary' : 'default'}
                  size="small"
                  sx={{
                    opacity: availableElements.has(el) ? 1 : 0.3,
                    pointerEvents: availableElements.has(el) ? 'auto' : 'none',
                    width: 36,
                    height: 32,
                    justifyContent: 'center',
                    backgroundColor: remainingElements.has(el) ? undefined : 'rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      backgroundColor: remainingElements.has(el) ? undefined : 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                />
              )
            )}
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button onClick={resetElements} variant="outlined" color="error" size="large">
            Reset
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} size="large" variant="outlined" color="error">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" size="large">
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
